import {
  conceptsDemoV1,
  instrumentDemoV1,
  likert5pV1,
} from "@/generated/assessment";
import type {
  MindstringPrototype,
  ThoughtDimensionAttributes,
  ThoughtDomain,
} from "@/generated/fastapi";
import type { GenericIconType } from "@/shared/ui/actions/GenericIcon";

export type Likert5pAgreementOption = {
  value: string;
  label: string;
  icon: GenericIconType;
  color: string;
};

const LIKERT_OPTION_ICONS = {
  "1": "likert_strongly_disagree",
  "2": "likert_disagree",
  "3": "likert_neutral",
  "4": "likert_agree",
  "5": "likert_strongly_agree",
} as const satisfies Record<string, GenericIconType>;
const LIKERT_OPTION_ICON_MAP: Record<string, GenericIconType> =
  LIKERT_OPTION_ICONS;

const LIKERT_5P_AGREEMENT_DATA: Likert5pAgreementOption[] = Object.entries(
  likert5pV1.options,
).map(([value, option]) => ({
  value,
  label: option.label,
  icon: LIKERT_OPTION_ICON_MAP[value] ?? "likert_neutral",
  color: option.color ?? "#95a5a6",
}));

const LIKERT_5P_AGREEMENT = Object.freeze(LIKERT_5P_AGREEMENT_DATA);

export function getLabelGivenScoreValue(value: string): string {
  const option = LIKERT_5P_AGREEMENT.find((opt) => opt.value === value);
  return option ? option.label : "Unknown";
}

export function getLikert5pAgreementOptions(): readonly Likert5pAgreementOption[] {
  return LIKERT_5P_AGREEMENT;
}

const INSTRUMENT_DEMO_UUID = instrumentDemoV1.instrument_id;

export type QuestionItem = {
  question: string;
};

export type InstrumentBattery = Record<
  ThoughtDimensionAttributes,
  QuestionItem
>;
type DomainInstruments = Record<ThoughtDomain, InstrumentBattery>;
export type InstrumentType = Record<string, Partial<DomainInstruments>>;

const INSTRUMENT: Readonly<InstrumentType> = Object.freeze({
  [INSTRUMENT_DEMO_UUID]:
    instrumentDemoV1.domains as Partial<DomainInstruments>,
});

export function getDefaultAssessmentInstrumentId(): string {
  return INSTRUMENT_DEMO_UUID;
}

export function getAssessmentQuestionForDimension({
  dimension,
  domain,
  instrumentId = INSTRUMENT_DEMO_UUID,
}: {
  dimension: ThoughtDimensionAttributes;
  domain: ThoughtDomain;
  instrumentId?: string;
}): QuestionItem | null {
  return INSTRUMENT[instrumentId]?.[domain]?.[dimension] ?? null;
}

export type AssessmentConcept = {
  concept_name: string;
  concept_description: string;
};
type AssessmentConceptByPrototype = Partial<
  Record<
    MindstringPrototype,
    Partial<Record<ThoughtDimensionAttributes, AssessmentConcept>>
  >
>;

const ASSESSMENT_CONCEPTS_BY_PROTOTYPE =
  conceptsDemoV1.prototypes as AssessmentConceptByPrototype;

export function getAssessmentConceptForDimension({
  dimension,
  prototype,
}: {
  dimension: ThoughtDimensionAttributes;
  prototype: MindstringPrototype;
}): AssessmentConcept | null {
  return ASSESSMENT_CONCEPTS_BY_PROTOTYPE[prototype]?.[dimension] ?? null;
}

export function getAssessmentDimensionLabel({
  dimension,
  prototype,
}: {
  dimension: ThoughtDimensionAttributes;
  prototype: MindstringPrototype;
}): string {
  return (
    getAssessmentConceptForDimension({ dimension, prototype })?.concept_name ??
    dimension
  );
}
